import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, TrendingUp, Lightbulb, ClipboardList, Users, LineChart } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { DjayAvatar, DjayBubble } from '../../../mascot';
import type { AccountRole } from '../../types';
import { OnboardingProgress } from './OnboardingProgress';
interface OnboardingWelcomeStepProps {
  role: AccountRole;
  name: string;
  groupLevel?: number;
  onNext: () => void;
  onGroupChange?: (group: number) => void;
}
export function OnboardingWelcomeStep({
  role,
  name,
  groupLevel = 6,
  onNext,
  onGroupChange
}: OnboardingWelcomeStepProps) {
  const getRoleConfig = () => {
    switch (role) {
      case 'learner':
        return {
          emotion: 'excited' as const,
          message: `Welkom ${name}! Ik ben Djay, jouw leermaatje. Samen gaan we oefenen en leren!`,
          gradient: 'from-orange-400 to-amber-400',
          border: 'border-orange-100',
          buttonBg: 'bg-orange-500 hover:bg-orange-600'
        };
      case 'parent':
        return {
          emotion: 'happy' as const,
          message: 'Welkom! Hier kunt u de voortgang van uw kind volgen.',
          gradient: 'from-emerald-400 to-teal-400',
          border: 'border-emerald-100',
          buttonBg: 'bg-emerald-600 hover:bg-emerald-700'
        };
      case 'teacher':
        return {
          emotion: 'celebrating' as const,
          message: 'Welkom! Laten we uw klas klaarzetten.',
          gradient: 'from-indigo-500 to-blue-400',
          border: 'border-indigo-100',
          buttonBg: 'bg-indigo-600 hover:bg-indigo-700'
        };
      default:
        return {
          emotion: 'happy' as const,
          message: 'Welkom!',
          gradient: 'from-slate-400 to-slate-500',
          border: 'border-slate-100',
          buttonBg: 'bg-slate-600 hover:bg-slate-700'
        };
    }
  };
  const config = getRoleConfig();
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full max-w-md mx-auto" data-id="element-298">
      {/* Mascot Section */}
      <div className="flex flex-col items-center mb-8 relative" data-id="element-299">
        <DjayBubble message={config.message} variant="welcome" className="mb-4" data-id="element-300" />
        <DjayAvatar emotion={config.emotion} size="lg" data-id="element-301" />
      </div>

      {/* Card */}
      <div className={`bg-white rounded-3xl p-8 shadow-xl border ${config.border} relative overflow-hidden`} data-id="element-302">
        <div className={`absolute top-0 left-0 w-full h-2 bg-gradient-to-r ${config.gradient}`} data-id="element-303" />

        <OnboardingProgress currentStep={0} role={role} data-id="element-304" />

        {role === 'learner' && <div className="space-y-6" data-id="element-305">
            <div className="text-center" data-id="element-306">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-307">
                Je zit in groep {groupLevel}
              </h2>
              <p className="text-gray-500 text-sm" data-id="element-308">
                Klopt dit? Je kunt het hier nog aanpassen.
              </p>
            </div>

            <div className="flex justify-center gap-3" data-id="element-309">
              {[6, 7, 8].map(g => <button key={g} onClick={() => onGroupChange?.(g)} className={`w-16 h-16 rounded-2xl text-xl font-bold transition-all ${groupLevel === g ? 'bg-orange-100 text-orange-600 ring-2 ring-orange-500 ring-offset-2' : 'bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`} data-id="element-310">
                  {g}
                </button>)}
            </div>
          </div>}

        {role === 'parent' && <div className="space-y-6" data-id="element-311">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-312">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-313">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-314">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-315">
                  <BarChart2 className="w-5 h-5 text-emerald-600" data-id="element-316" />
                </div>
                <span data-id="element-317">Voortgang per vak volgen</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-318">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-319">
                  <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-320" />
                </div>
                <span data-id="element-321">Groei over tijd bekijken</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-322">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-323">
                  <Lightbulb className="w-5 h-5 text-emerald-600" data-id="element-324" />
                </div>
                <span data-id="element-325">Persoonlijke oefentips</span>
              </li>
            </ul>
          </div>}

        {role === 'teacher' && <div className="space-y-6" data-id="element-326">
            <h2 className="text-xl font-bold text-center text-gray-900" data-id="element-327">
              Wat kunt u hier doen?
            </h2>
            <ul className="space-y-4" data-id="element-328">
              <li className="flex items-center gap-3 text-gray-700" data-id="element-329">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-330">
                  <ClipboardList className="w-5 h-5 text-indigo-600" data-id="element-331" />
                </div>
                <span data-id="element-332">Dagelijks klas-overzicht</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-333">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-334">
                  <Users className="w-5 h-5 text-indigo-600" data-id="element-335" />
                </div>
                <span data-id="element-336">Leerlingen beheren</span>
              </li>
              <li className="flex items-center gap-3 text-gray-700" data-id="element-337">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-338">
                  <LineChart className="w-5 h-5 text-indigo-600" data-id="element-339" />
                </div>
                <span data-id="element-340">Groei en trends volgen</span>
              </li>
            </ul>
          </div>}

        <div className="mt-8" data-id="element-341">
          <Button onClick={onNext} className={`w-full py-6 text-lg text-white ${config.buttonBg}`} data-id="element-342">
            {role === 'learner' ? 'Klopt! Verder' : 'Verder'}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-343" />
          </Button>
        </div>
      </div>
    </motion.div>;
}